import { api } from '@goaltree/backend/convex/_generated/api'
import { DAY_MS, PLAN_PRICES_USD } from '@goaltree/backend/convex/lib/plan'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useQuery } from 'convex/react'
import { Bell, Check, Lock, Sparkles } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { PlanCard } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { usePaywallVariant } from '@/hooks/use-paywall-variant'
import { usePolar } from '@/hooks/use-polar'

type Term = 'year' | 'month'

export const Route = createFileRoute('/onboarding/paywall')({
  // Where onboarding would have landed: Today, or Everyday for a habits focus.
  validateSearch: (search): { next?: 'today' | 'habits' } => ({
    next: search.next === 'today' || search.next === 'habits' ? search.next : undefined,
  }),
  head: () => ({
    meta: [{ title: 'Unlock Pro — Goaltree' }],
  }),
  component: PaywallComponent,
})

// Last onboarding step in the trial-paywall arm (and where pricing-page signups
// land): a card trial on yearly, monthly as the alternative, or stay on Free.
function PaywallComponent() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const posthog = usePostHog()
  const search = Route.useSearch()
  const { isPro, isLoading, cardTrialEligible } = useIsPro()
  const { checkout } = usePolar()
  const variant = usePaywallVariant()
  const [term, setTerm] = useState<Term>('year')
  const [loading, setLoading] = useState(false)
  const { isAuthenticated } = useConvexAuth()
  const pricing = useQuery(api.subscriptions.getPricing, {})
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, isAuthenticated ? {} : 'skip')
  // Pricing-page signups arrive here before onboarding — continue into it.
  // Same condition as the _authenticated onboarding guard.
  const next =
    prefs &&
    goals &&
    !(prefs as { onboardingCompletedAt?: number }).onboardingCompletedAt &&
    !goals.some((g) => !g.isInbox)
      ? '/goals/new'
      : '/goals'
  function leave() {
    if (search.next === 'habits')
      navigate({ to: '/everyday', search: { tab: 'habits' }, replace: true })
    else navigate({ to: search.next === 'today' ? '/today' : next, replace: true })
  }

  const seen = useRef(false)
  useEffect(() => {
    if (seen.current || isLoading || isPro) return
    seen.current = true
    posthog.capture('paywall_seen', { surface: 'onboarding', paywall_variant: variant })
  }, [isLoading, isPro])

  useEffect(() => {
    if (!isLoading && isPro) leave()
  }, [isPro, isLoading, next])

  const prices = pricing?.prices ?? PLAN_PRICES_USD
  const trialDays = pricing ? pricing.trialDays[term] : 7
  const trial = cardTrialEligible && trialDays > 0
  const money = (n: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: pricing?.currency ?? 'USD',
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n)
  const day = (offset: number) =>
    new Date(Date.now() + offset * DAY_MS).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    })
  const endsOn = day(trialDays)
  const savePct = Math.round((1 - prices.year / (prices.month * 12)) * 100)
  const price = money(prices[term])
  const fine = trial
    ? t(term === 'year' ? 'trialPaywall.fineTrialYear' : 'trialPaywall.fineTrialMonth', {
        date: endsOn,
        price,
      })
    : t(term === 'year' ? 'trialPaywall.fineYear' : 'trialPaywall.fineMonth', { price })

  function continueFree() {
    posthog.capture('paywall_dismissed', { surface: 'onboarding', paywall_variant: variant })
    leave()
  }

  async function start() {
    if (pricing && !pricing.checkout[term]) {
      toast.error(t('upgrade.fs.planUnavailable'), {
        description: t('upgrade.fs.planUnavailableHint'),
      })
      return
    }
    setLoading(true)
    const ok = await checkout(term, { trial: true, viaApi: pricing?.polar.month })
    if (!ok) setLoading(false)
  }

  if (isLoading || isPro) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  const steps = [
    { icon: Lock, label: t('trialPaywall.todayLabel'), desc: t('trialPaywall.todayDesc') },
    { icon: Bell, label: day(trialDays - 1), desc: t('trialPaywall.remindDesc') },
    { icon: Sparkles, label: endsOn, desc: t('trialPaywall.chargeDesc') },
  ]

  return (
    <div className="relative flex min-h-dvh flex-col bg-background">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-linear-to-b from-primary/22 via-primary/6 to-transparent" />
      <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col px-5 pt-[calc(env(safe-area-inset-top,0px)+40px)]">
        <span className="flex size-12 items-center justify-center rounded-[14px] bg-primary/12 text-primary-text">
          <Sparkles className="size-6" />
        </span>
        <p className="mt-4 text-[10.5px] font-extrabold tracking-[0.14em] text-primary-text uppercase">
          {t('trialPaywall.eyebrow')}
        </p>
        <h1 className="mt-1 text-[28px] leading-[1.1] font-black tracking-tight text-balance">
          {trial ? t('trialPaywall.title', { days: trialDays }) : t('trialPaywall.titleNoTrial')}
        </h1>
        <p className="mt-2 text-[14px] text-muted-foreground">{t('trialPaywall.subtitle')}</p>

        {trial && (
          <ol className="mt-6 flex flex-col">
            {steps.map(({ icon: Icon, label, desc }, i) => (
              <li key={label} className="flex gap-3">
                <span className="flex flex-col items-center">
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-full',
                      i === 0
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-primary/12 text-primary-text',
                    )}
                  >
                    <Icon className="size-4" />
                  </span>
                  {i < steps.length - 1 && <span className="my-1 w-0.5 flex-1 bg-primary/20" />}
                </span>
                <span className="pb-4">
                  <b className="block text-[14px] font-bold">{label}</b>
                  <span className="text-[12.5px] text-muted-foreground">{desc}</span>
                </span>
              </li>
            ))}
          </ol>
        )}

        <ul className={cn('flex flex-col gap-2', trial ? 'mt-1' : 'mt-6')}>
          {(
            ['featureUnlimitedGoals', 'featureCoachVoice', 'featureVoice', 'featureAI'] as const
          ).map((k) => (
            <li key={k} className="flex items-center gap-2.5 text-[13.5px]">
              <Check className="size-4 shrink-0 text-primary-text" strokeWidth={3} />
              {t(`paywall.${k}`)}
            </li>
          ))}
        </ul>

        <div role="radiogroup" className="mt-6 flex flex-col gap-3 pb-6">
          <PlanCard
            selected={term === 'year'}
            onSelect={() => setTerm('year')}
            name={t('trialPaywall.planYear')}
            note={t('trialPaywall.planYearNote', { price: money(prices.year) })}
            price={money(Math.round((prices.year / 12) * 100) / 100)}
            unit={t('trialPaywall.perMonth')}
            badge={savePct > 0 ? t('trialPaywall.save', { pct: savePct }) : undefined}
          />
          <PlanCard
            selected={term === 'month'}
            onSelect={() => setTerm('month')}
            name={t('trialPaywall.planMonth')}
            note={t('trialPaywall.planMonthNote')}
            price={money(prices.month)}
            unit={t('trialPaywall.perMonth')}
          />
        </div>
      </div>

      <div className="sticky bottom-0 border-t border-border/70 bg-background/95 backdrop-blur">
        <div className="mx-auto w-full max-w-md px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)]">
          <button
            type="button"
            onClick={start}
            disabled={loading}
            className="inline-flex h-13 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15.5px] font-extrabold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('paywall.openingCheckout')}
              </>
            ) : trial ? (
              t('trialPaywall.ctaTrial', { days: trialDays })
            ) : (
              t(term === 'year' ? 'trialPaywall.ctaYear' : 'trialPaywall.ctaMonth')
            )}
          </button>
          <p className="mt-2 text-center text-[11.5px] text-muted-foreground/80">{fine}</p>
          <button
            type="button"
            onClick={continueFree}
            className="w-full cursor-pointer pt-2 text-center text-[13.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('trialPaywall.continueFree')}
          </button>
        </div>
      </div>
    </div>
  )
}
